import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Tooltip,
  Divider,
  Chip,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search as SearchIcon, Refresh as RefreshIcon, Visibility as ViewIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, SAPComboBox, PageLayout, FilterBar } from '../components';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { usePermissions } from '../api/auth/PermissionsContext';
import { fetchJobRuns, fetchJobRunsCount } from '../api/features/jobRun/jobRunApi';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails, fetchJobNames } from '../api/features/jobDetail/jobDetailApi';
import { fetchErrorCodeCount } from '../api/features/integrationFailures/integrationApi';
import type { Client } from '../api/features/clientData/client.types';
import type { JobRunData } from '../api/features/jobRun/jobRun.types';

const ROWS_PER_PAGE = 10;

// SAP Styled Components

const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));

const SAPTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '4px',
    '& fieldset': {
      borderColor: SAP_COLORS.sapGray3,
    },
    '&:hover fieldset': {
      borderColor: SAP_COLORS.sapBlue,
    },
    '&.Mui-focused fieldset': {
      borderColor: SAP_COLORS.sapBlue,
      boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightBlue}`,
    },
  },
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
  '& .MuiInputBase-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
  },
}));

const SAPStatusChip = styled(Chip)(({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    if (status?.toLowerCase().includes('success') || status?.toLowerCase().includes('complete')) {
      return { bg: SAP_COLORS.sapLightGreen, text: SAP_COLORS.sapGreen };
    }
    if (status?.toLowerCase().includes('error') || status?.toLowerCase().includes('fail')) {
      return { bg: SAP_COLORS.sapLightRed, text: SAP_COLORS.sapRed };
    }
    if (status?.toLowerCase().includes('running') || status?.toLowerCase().includes('active')) {
      return { bg: SAP_COLORS.sapLightBlue, text: SAP_COLORS.sapBlue };
    }
    return { bg: SAP_COLORS.sapLightOrange, text: SAP_COLORS.sapOrange };
  };
  const colors = getStatusColor(status);
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: '0.75rem',
  };
});

export default function JobRun() {
  const { clientCode } = useClientCode();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const isAdmin = clientCode === 'admin';

  // State
  const [jobRuns, setJobRuns] = useState<JobRunData[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);

  // Filter states
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [jobOptions, setJobOptions] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Modal state for error code count
  const [errorCodeModalOpen, setErrorCodeModalOpen] = useState(false);
  const [errorCodeData, setErrorCodeData] = useState<any[]>([]);
  const [loadingErrorCodes, setLoadingErrorCodes] = useState(false);
  const [selectedJobRun, setSelectedJobRun] = useState<JobRunData | null>(null);

  // Load client options on component mount (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clients) => {
        setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClientOptions([]));
  }, [clientCode, isAdmin]);

  // Load job options
  // - Admin: when a client is selected
  // - Client users: all jobs for their clientCode (no explicit client selector)
  useEffect(() => {
    if (isAdmin) {
      if (selectedClient && selectedClient.value) {
        fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
          .then((jobs) =>
            setJobOptions(
              jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
            )
          )
          .catch(() => setJobOptions([]));
        setSelectedJob(null);
      } else {
        setJobOptions([]);
        setSelectedJob(null);
      }
      return;
    }

    // Client users: fetch jobs scoped by their clientCode
    fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 })
      .then((jobs) =>
        setJobOptions(
          jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))
        )
      )
      .catch(() => setJobOptions([]));
  }, [selectedClient, clientCode, isAdmin]);

  const handleClientChange = (event: any, newValue: any) => {
    setSelectedClient(newValue);
  };

  const loadJobRuns = async (pageOverride?: number) => {
    setLoading(true);
    
    try {
      const params: any = {
        firstResult: (pageOverride ?? page) * ROWS_PER_PAGE,
        maxResult: ROWS_PER_PAGE,
        searched: true
      };

      if (selectedClient?.value) {
        params.clientId = selectedClient.value;
      }
      if (selectedJob?.value) {
        // selectedJob.value now contains the jobId directly
        params.jobDetailId = selectedJob.value;
        logger.log('Using jobDetailId:', selectedJob.value, 'for job:', selectedJob.label);
      }
      if (fromDate) {
        params.startDate = fromDate.toISOString();
        params.startDateValue = fromDate.toLocaleDateString('en-GB');
      }
      if (toDate) {
        params.endDate = toDate.toISOString();
        params.endDateValue = toDate.toLocaleDateString('en-GB');
      }

      logger.log('JobRun API params:', params);

      const [data, count] = await Promise.all([
        fetchJobRuns({
          clientCode,
          ...params
        }),
        fetchJobRunsCount({
          clientCode,
          clientId: selectedClient?.value,
          jobDetailId: params.jobDetailId,
          startDate: params.startDate,
          endDate: params.endDate,
          startDateValue: params.startDateValue,
          endDateValue: params.endDateValue,
          searched: true
        })
      ]);

      logger.log('JobRun API Response:', { data, count, dataLength: data?.length, countType: typeof count });
      setJobRuns(data);
      setRowCount(count || 0);
      logger.log('Set rowCount to:', count || 0);
    } catch (err: any) {
      logger.error('JobRun API Error:', err);
      toast.error('Failed to load job runs');
      setJobRuns([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadJobRuns(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setSelectedJob(null);
    setFromDate(null);
    setToDate(null);
    setJobRuns([]);
    setRowCount(0);
    setPage(0);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    loadJobRuns(newPage);
  };

  const formatDateTime = (timestamp: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleFailureCountClick = async (jobRun: JobRunData) => {
    if (!jobRun.id || jobRun.failureCount === 0) return;
    
    setSelectedJobRun(jobRun);
    setErrorCodeModalOpen(true);
    setLoadingErrorCodes(true);
    setErrorCodeData([]);
    
    try {
      const data = await fetchErrorCodeCount({ clientCode, jobRunId: jobRun.id });
      setErrorCodeData(data);
    } catch (err: any) {
      logger.error('Error fetching error code count:', err);
      toast.error('Failed to load error code count');
      setErrorCodeData([]);
    } finally {
      setLoadingErrorCodes(false);
    }
  };

  const handleViewIntegrationFailures = (errorCode: string) => {
    if (!selectedJobRun?.id) return;
    
    // Open IntegrationInstanceFailure in a new tab with query params
    const url = `/integration-failure?errorCode=${encodeURIComponent(errorCode)}&jobRunId=${selectedJobRun.id}`;
    window.open(url, '_blank');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageLayout
        title="Job Run"
        description="Search and view job execution details and performance metrics"
        titleSx={{ color: SAP_COLORS.sapBlue }}
      >
        <FilterBar loading={loading}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            flex: 1,
            alignItems: 'center',
            width: '100%',
          }}>
            {/* Row 1 - Entity Filters: Client, Job */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
              {/* Client Dropdown - Hidden for non-admin */}
              {isAdmin && (
                <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
                  <Autocomplete
                    options={clientOptions}
                    getOptionLabel={(option) => option.label}
                    value={selectedClient}
                    onChange={handleClientChange}
                    renderInput={(params) => (
                      <TextField {...params} label="Client Name" />
                    )}
                  />
                </Box>
              )}

              {/* Job Name Dropdown */}
              <Box sx={{ flex: { xs: '1 1 100%', sm: isAdmin ? '1 1 calc(50% - 8px)' : '1 1 100%', md: isAdmin ? '1 1 calc(50% - 8px)' : '1 1 100%' } }}>
                <Autocomplete
                  options={jobOptions}
                  getOptionLabel={(option) => option.label}
                  value={selectedJob}
                  onChange={(_, newValue) => setSelectedJob(newValue)}
                  disabled={isAdmin && !selectedClient}
                  renderInput={(params) => (
                    <TextField {...params} label="Job Name" />
                  )}
                />
              </Box>
            </Box>

            {/* Row 2 - Date Range: From Date, To Date */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
            {/* From Date */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
              <DateTimePicker
                label="From Date"
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                ampm={false}
                views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                format="dd/MM/yyyy HH:mm:ss"
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>

            {/* To Date */}
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(50% - 8px)' } }}>
              <DateTimePicker
                label="To Date"
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                ampm={false}
                views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                format="dd/MM/yyyy HH:mm:ss"
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Box>
            </Box>

            {/* Search and Reset Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <SAPIconButton
                icon="search"
                variant="primary"
                onClick={handleSearch}
                disabled={loading}
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                variant="secondary"
                onClick={handleReset}
                disabled={loading}
                aria-label="Reset"
              />
            </Box>
          </Box>
        </FilterBar>

        {/* Data Display */}
        <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent sx={{ padding: 0 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <CircularProgress 
                size={40} 
                sx={{ color: SAP_COLORS.sapBlue }} 
              />
            </Box>
          ) : jobRuns.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              padding: SAP_SPACING.xl 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  marginBottom: SAP_SPACING.md,
                }}
              >
                No job runs found
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Try adjusting your search criteria or click Search to load data
              </Typography>
            </Box>
          ) : (
            <>
              {/* SAP Table */}
              <TableContainer 
                component={Paper} 
                sx={{ 
                  boxShadow: 'none', 
                  backgroundColor: SAP_COLORS.sapWhite 
                }}
              >
                <Table sx={{ minWidth: 900 }} stickyHeader aria-label="job run table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: SAP_COLORS.sapLightBlue }}>
                      <SAPTableHeader>S.No.</SAPTableHeader>
                      <SAPTableHeader>Job Name</SAPTableHeader>
                      <SAPTableHeader>Start Time</SAPTableHeader>
                      <SAPTableHeader>End Time</SAPTableHeader>
                      <SAPTableHeader>File Name</SAPTableHeader>
                      <SAPTableHeader>Success Count</SAPTableHeader>
                      <SAPTableHeader>Failure Count</SAPTableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ backgroundColor: SAP_COLORS.sapWhite }}>
                    {jobRuns.map((jobRun, idx) => (
                      <TableRow
                        key={jobRun.id}
                        sx={{
                          backgroundColor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                          '&:hover': { 
                            backgroundColor: SAP_COLORS.hover,
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray6,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {page * ROWS_PER_PAGE + idx + 1}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray7,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {jobRun.jobName}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray6,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {formatDateTime(jobRun.startTime)}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray6,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {jobRun.endTime ? formatDateTime(jobRun.endTime) : '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray6,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {jobRun.fileName || 'Data will be uploaded in next run.'}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGreen,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                        }}>
                          {jobRun.successCount || 0}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapRed,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                          cursor: (jobRun.failureCount || 0) > 0 ? 'pointer' : 'default',
                          '&:hover': (jobRun.failureCount || 0) > 0 ? {
                            textDecoration: 'underline',
                            color: SAP_COLORS.sapRed,
                          } : {},
                        }}
                        onClick={() => handleFailureCountClick(jobRun)}
                      >
                        {jobRun.failureCount || 0}
                      </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* SAP Pagination */}
              {/* Pagination */}
              {jobRuns.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + jobRuns.length, rowCount)} of {rowCount} job runs
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(rowCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
        </Card>
      </PageLayout>

      {/* Error Code Count Modal */}
      <Dialog
        open={errorCodeModalOpen}
        onClose={() => setErrorCodeModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: SAP_COLORS.sapWhite,
            boxShadow: SAP_SHADOWS.lg,
            borderRadius: 2,
            maxHeight: '90vh',
            m: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: SAP_COLORS.sapGray1, 
            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`, 
            pb: 1.5,
            px: 2,
            py: 1.5,
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: SAP_TYPOGRAPHY.fontWeight.bold, 
                color: SAP_COLORS.sapGray7,
                fontSize: '1rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                pr: 2,
              }}
            >
              Error Code Count - {selectedJobRun?.jobName || 'Job Run'}
            </Typography>
            <IconButton 
              onClick={() => setErrorCodeModalOpen(false)}
              size="small"
              sx={{ 
                color: SAP_COLORS.sapGray6,
                p: 0.5,
                flexShrink: 0,
                '&:hover': { 
                  bgcolor: SAP_COLORS.hover,
                  color: SAP_COLORS.sapGray7
                }
              }}
            >
              <CloseIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: SAP_COLORS.sapWhite,
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: SAP_COLORS.sapGray1,
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: SAP_COLORS.sapGray3,
              borderRadius: '3px',
              '&:hover': {
                bgcolor: SAP_COLORS.sapGray4,
              }
            }
          }}
        >
          {loadingErrorCodes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
              <CircularProgress size={32} sx={{ color: SAP_COLORS.sapBlue }} />
            </Box>
          ) : errorCodeData.length === 0 ? (
            <Box sx={{ textAlign: 'center', padding: 3 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                No error codes found
              </Typography>
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: 'none', 
                backgroundColor: SAP_COLORS.sapWhite,
                borderRadius: 0,
                width: '100%',
              }}
            >
              <Table 
                size="small"
                sx={{ 
                  width: '100%',
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': { 
                    padding: '8px 12px',
                    borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    padding: '10px 12px',
                  }
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: SAP_COLORS.sapLightBlue }}>
                    <SAPTableHeader sx={{ padding: '10px 12px !important', width: '50%' }}>Error Code</SAPTableHeader>
                    <SAPTableHeader sx={{ padding: '10px 12px !important', width: '25%', textAlign: 'center' }}>Count</SAPTableHeader>
                    <SAPTableHeader sx={{ padding: '10px 12px !important', width: '25%', textAlign: 'center' }}>Actions</SAPTableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorCodeData.map((item: any, index: number) => {
                    const errorCode = item.errorCode || item.code || item[0] || 'Unknown';
                    const count = item.count || item[1] || item.count || 0;
                    return (
                      <TableRow 
                        key={index}
                        sx={{
                          backgroundColor: index % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                          '&:hover': { 
                            backgroundColor: SAP_COLORS.hover,
                          },
                          '&:last-child td': {
                            borderBottom: 0,
                          }
                        }}
                      >
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapGray7,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          padding: '8px 12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {errorCode}
                        </TableCell>
                        <TableCell sx={{ 
                          color: SAP_COLORS.sapRed,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                          padding: '8px 12px',
                          textAlign: 'center',
                        }}>
                          {count}
                        </TableCell>
                        <TableCell sx={{ 
                          padding: '4px 12px',
                          textAlign: 'center',
                        }}>
                          <IconButton
                            onClick={() => handleViewIntegrationFailures(errorCode)}
                            size="small"
                            sx={{
                              color: SAP_COLORS.sapBlue,
                              padding: '4px',
                              minWidth: '28px',
                              width: '28px',
                              height: '28px',
                              '&:hover': {
                                bgcolor: SAP_COLORS.sapLightBlue,
                                color: SAP_COLORS.sapBlue,
                              }
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        </Dialog>
    </LocalizationProvider>
  );
} 